import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { errMsg, fmtDate } from "@/lib/format";
import { PageHeader, ErrorState, LoadingRows, NoAccess, EmptyState, Field } from "@/components/common/States";
import { Pick, Table, Stat, StatusBadge, money, label } from "./shared";
import { RecordExpenseDialog } from "./GarageKpis";

const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

export default function Expenses() {
  const { can, api, branches } = useSession();
  const nav = useNavigate();
  const [f, setF] = useState({ from: monthStart(), to: today(), branch_id: "", q: "" });
  const [rec, setRec] = useState(false);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const { data, loading, error, reload } = useFetch("/reports/expenses", params, can("report.view"));
  if (!can("report.view")) return <NoAccess perm="report.view" />;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const exportCsv = async () => {
    try {
      const r = await api.get("/reports/expenses.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `garage39-expenses-${f.from}-${f.to}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cols = [
    ["Date", (r) => fmtDate(r.service_date), "whitespace-nowrap"],
    ["Service no", (r) => <span className="font-mono-g">{r.number}</span>],
    ["Vehicle", (r) => <span className="font-mono-g">{r.registration_number}</span>],
    ["Branch", (r) => r.branch_name || "—"],
    ["Type", (r) => <span className="capitalize">{label(r.service_type)}</span>],
    ["Labour", (r) => money(r.labor), "text-right"],
    ["Parts", (r) => money(r.parts), "text-right"],
    ["External", (r) => money(r.external), "text-right"],
    ["Total", (r) => <b>{money(r.total)}</b>, "text-right"],
    ["Bills", (r) => r.attachments, "text-right"],
    ["Status", (r) => <StatusBadge s={r.status} />],
  ];
  const vcols = [
    ["Vehicle", (r) => <span className="font-mono-g font-semibold">{r.registration_number}</span>],
    ["Services", (r) => r.services, "text-right"],
    ["Labour", (r) => money(r.labor), "text-right"], ["Parts", (r) => money(r.parts), "text-right"],
    ["External", (r) => money(r.external), "text-right"], ["Total", (r) => <b>{money(r.total)}</b>, "text-right"],
  ];
  const t = data?.totals;
  return (
    <div data-testid="expenses-page">
      <PageHeader title="Expenses" subtitle="Costs from approved services. Drafts and cancelled services are not counted."
        actions={<>{can("maintenance.manage") && <Button onClick={() => setRec(true)} data-testid="expenses-record-btn">Record expense</Button>}{can("report.export") && <Button variant="outline" onClick={exportCsv} disabled={!data} data-testid="export-csv-btn"><Download className="h-4 w-4 mr-1.5" />Export CSV</Button>}</>} />
      <RecordExpenseDialog open={rec} onOpenChange={setRec} />
      <div className="g39-card p-4 mb-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="From"><Input type="date" value={f.from} onChange={set("from")} data-testid="expenses-from" /></Field>
        <Field label="To"><Input type="date" value={f.to} onChange={set("to")} data-testid="expenses-to" /></Field>
        <Field label="Branch">{branches.length > 1 ? <Pick value={f.branch_id} onChange={set("branch_id")} allLabel="All branches" className="w-full" options={branches.map((b) => ({ value: b.id, label: b.name }))} testId="expenses-branch" /> : <Input disabled value={branches[0]?.name || "—"} />}</Field>
        <Field label="Registration number"><Input value={f.q} placeholder="e.g. KA01" onChange={set("q")} data-testid="expenses-reg" /></Field>
      </div>
      {loading && <div className="g39-card"><LoadingRows rows={4} /></div>}
      {error && <div className="g39-card"><ErrorState message={error} onRetry={reload} /></div>}
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Stat label="Labour cost" value={money(t.labor)} testId="exp-labor" />
            <Stat label="Parts cost" value={money(t.parts)} testId="exp-parts" />
            <Stat label="External garage charges" value={money(t.external)} testId="exp-external" />
            <Stat label={`Total maintenance (${t.services} services)`} value={money(t.total)} tone="text-gold" testId="exp-total" />
          </div>
          <div className="g39-card mb-6" data-testid="expenses-by-vehicle">
            <div className="px-5 py-3 border-b font-display font-semibold">By vehicle</div>
            {!data.by_vehicle.length ? <EmptyState title="No expenses in this period" testId="expenses-empty" /> : <Table cols={vcols} rows={data.by_vehicle.map((x) => ({ ...x, id: x.vehicle_id }))} testId="by-vehicle-table" onRow={(r) => nav(`/service-timeline/${r.vehicle_id}`)} />}
          </div>
          {data.rows.length > 0 && <div className="g39-card"><div className="px-5 py-3 border-b font-display font-semibold">Service records</div><Table cols={cols} rows={data.rows} testId="expense-rows-table" onRow={(r) => nav(`/service-records/${r.id}`)} /></div>}
        </>
      )}
    </div>
  );
}
